import { z } from 'zod';

import { type AstInput, embeddedString } from '../normalize.ts';
import type { CompileFn, RawOpCall, RowFn } from '../op-meta.ts';
import { toComparableNumber } from '../ops/transform/date-utils.ts';
import { paramPath } from '../path.ts';
import { booleanCol, type ColumnType, numberCol, unknownCol } from '../schema/types.ts';
import type { UnitPolicy } from '../schema/unit-propagation.ts';
import { cellNumber, unitCell, unitOfCell, type Value } from '../value.ts';

// Built-in ops as Zod schemas. The schema describes the op's name + arg
// shape; `.describe()` carries the LLM-facing summary; `.register(opMeta,
// ...)` attaches `apply` plus the registry-only fields (evaluation, effect,
// example, compileExpr).
//
// `apply`'s `data` argument is precisely typed per-schema via Zod's
// `$output` substitution — no `z.infer<typeof X>` annotation needed.
// `apply`'s RETURN is typed loosely (`unknown`/`Promise<unknown>`) in the
// registry-stored shape; authors annotate the arrow's return type
// (`: Value`, `: Promise<EvalResult>`, `: Dataset`, …) at the call site so
// the body is checked against the engine's runtime contract.

// Compile-helper: walk each positional arg of an op call to a RowFn. Used
// by every scalar op's compileExpr method. Awaits each compiled child in
// parallel via Promise.all. Generic `T` lets fixed-arity callers narrow
// the return to a tuple, e.g.
//   const [a, b] = await compileArgs<readonly [RowFn, RowFn]>(call, compile, path);
// — safe because the schema validates arity before compileExpr runs.
export const compileArgs = async <T extends readonly RowFn[] = readonly RowFn[]>(
  call: RawOpCall,
  compile: CompileFn,
  path: string,
): Promise<T> => (await Promise.all((call.params ?? []).map((p, i) => compile(p, paramPath(path, i))))) as unknown as T;
// Schema-fragment: comparison chain params (≥2). Shared by chain-comparison
// ops `<`, `<=`, `>`, `>=`. Operands are coerced date-aware via
// `toComparableNumber` (numeric semantics unchanged; ISO/offset date
// strings rescued to epoch-ms) so a `timestamp` column compares correctly
// against a string-literal date bound — same coercer the `compileExpr`
// (filter/derive per-row) path uses, keeping the two paths in sync.
export const numChainParams = z.unknown().transform(toComparableNumber).array().min(2);
// Op-helpers: produce a paired `{ apply, compileExpr }` from a single
// evaluation kernel (`fold`) so apply (over schema-resolved values) and
// compileExpr (over per-row values) stay in sync. The optional `coerce`
// callback converts operands to the reducer's value type on BOTH paths —
// every coercer in use is idempotent, so schema-coerced apply params pass
// through unchanged.

// The `data` parameter of each helper-built `apply` is typed loosely as
// `{ params: readonly unknown[] }` (rather than `{ params: readonly
// ValueType[] }`) because the registry-stored apply signature is
// `(data: $output) => unknown` — function arg contravariance requires
// the helper accept whatever shape the registry passes. The schema
// validates params to the right ValueType before apply runs, so the
// internal cast is sound.
//
// `defineFold` is the structural meta-helper; the arity-specific variants
// (`defineUnary`, `defineBinary`, `defineChain`) are thin wrappers that
// supply a specialized reducer. The `values[i] as ValueType` casts inside
// each wrapper's reducer are safe because the op schema validates arity
// before either path runs.

/** Variadic fold (`+`, `*`, `and`, `or`, `coalesce`, `-`, `/`); also the
 * meta-helper that `defineUnary`/`defineBinary`/`defineChain` wrap.
 * `resultType` is the op's `ColumnType` for the pre-eval type check —
 * defaults to `unknownCol`; callers with a fixed result type (number
 * for arithmetic, boolean for comparison) pass the constant here. Ops
 * with computed types (e.g. `coalesce`) override the generated
 * `inferType` at the `.register(...)` site. An optional `unitPolicy`
 * derives the result's unit from the operands' per-row units at VALUE
 * level (see `unit-propagation.ts`): a numeric result that earns a unit
 * boxes to a `UnitCell`; null/boolean results always pass through
 * unboxed. Absent ⇒ the result carries no unit. */
export const defineFold = <ValueType = unknown>(
  reducer: (values: readonly ValueType[]) => Value,
  coerce: (value: unknown) => ValueType = (v) => v as ValueType,
  resultType: ColumnType = unknownCol,
  unitPolicy?: UnitPolicy,
) => {
  // Single evaluation kernel shared by the eager apply path and the
  // per-row compileExpr path — the one place operand coercion and
  // value-level unit propagation happen.
  const fold = (raw: readonly unknown[]): Value => {
    const result = reducer(raw.map(coerce));
    if (unitPolicy === undefined || typeof result !== 'number') {
      return result;
    }
    const unit = unitPolicy(raw.map(unitOfCell));
    return unit === undefined ? result : unitCell(result, unit);
  };
  return {
    evaluation: 'eager' as const,
    effect: 'pure' as const,
    apply: ({ params }: { readonly params: readonly unknown[] }): Value => fold(params),
    expr: {
      compileExpr: async (call: RawOpCall, compile: CompileFn, path: string): Promise<RowFn> => {
        const args = await compileArgs(call, compile, path);
        return (row) => fold(args.map((f) => f(row)));
      },
      // Units are propagated at value level, never stamped statically.
      inferType: (): ColumnType => resultType,
    },
  };
};

/** Arity-1 scalar op (`not`, `is-null`, `is-not-null`). */
export const defineUnary = <ValueType = unknown>(
  fn: (value: ValueType) => Value,
  coerce?: (value: unknown) => ValueType,
  resultType?: ColumnType,
) => defineFold<ValueType>((values) => fn(values[0] as ValueType), coerce, resultType);
/** Arity-2 scalar op (`mod`, `!=`). */
export const defineBinary = <ValueType = unknown>(
  fn: (a: ValueType, b: ValueType) => Value,
  coerce?: (value: unknown) => ValueType,
  resultType?: ColumnType,
  unitPolicy?: UnitPolicy,
) =>
  defineFold<ValueType>((values) => fn(values[0] as ValueType, values[1] as ValueType), coerce, resultType, unitPolicy);
// Stringify for prefix tests. A `Date` cell (a `timestamp` column)
// renders as its ISO-8601 string — so `(prefix createdTime "2025")`
// does the obvious thing instead of testing against the JS locale
// string `"Wed Oct 01 2025…"`. Everything else is plain `String(v)`,
// unchanged (ISO-string cells already startsWith correctly).
export const prefixStringify = (v: unknown): string => (v instanceof Date ? v.toISOString() : String(v));
/**
 * Compile a binary string-op `(op str pattern)` where building a matcher
 * (RegExp, Fuse instance, …) is expensive relative to per-row `test`.
 * Hoists matcher construction out of the row loop:
 *
 *   - Compile-time literal pattern → matcher built ONCE at compile,
 *     baked into the per-row function. Zero per-row construction
 *     overhead.
 *   - Dynamic pattern (column ref / expression) → matcher built lazily
 *     on first row and memoised by stringified pattern; the cache
 *     invalidates correctly when the pattern itself changes per row,
 *     but the common case (pattern is constant or row-constant) reuses
 *     a single matcher across the full filter.
 *
 * Shared SSOT for `re-match` and `fuzzy` so the row-loop never
 * instantiates a fresh RegExp / Fuse on every row — load-bearing for
 * 50k-row knowledge-graph filters where per-row construction would
 * dominate the budget.
 *
 * Cache atomicity: `buildMatcher` is called BEFORE `cachedPat` is
 * updated. A throw therefore leaves the cache entirely untouched (still
 * pointing at the prior successful pat/matcher) — the next row with the
 * same bad pat re-attempts the build and re-throws, rather than
 * silently reusing a stale matcher tied to a different pattern.
 */
export const compileHoistedBinary = async <MatcherType>(
  call: RawOpCall,
  compile: CompileFn,
  path: string,
  buildMatcher: (pattern: string) => MatcherType,
  test: (matcher: MatcherType, str: string) => boolean,
): Promise<RowFn> => {
  const params = call.params as readonly AstInput[];
  const [strArg, patArg] = params;
  const strFn = await compile(strArg, paramPath(path, 0));
  const patLiteral = embeddedString(patArg);
  if (patLiteral !== undefined) {
    const matcher = buildMatcher(patLiteral);
    return (row) => test(matcher, prefixStringify(strFn(row)));
  }
  const patFn = await compile(patArg, paramPath(path, 1));
  let cachedPat: string | undefined;
  let cachedMatcher: MatcherType | undefined;
  return (row) => {
    const pat = prefixStringify(patFn(row));
    if (pat !== cachedPat || cachedMatcher === undefined) {
      // Build first, assign second — a throw leaves the cache untouched
      // so the next attempt re-builds (and re-throws) instead of
      // silently reusing a matcher tied to a different pat.
      const next = buildMatcher(pat);
      cachedMatcher = next;
      cachedPat = pat;
    }
    return test(cachedMatcher, prefixStringify(strFn(row)));
  };
};
/** Variadic pairwise chain (`<`, `<=`, `>`, `>=`, `=`). True iff every adjacent
 * pair `(values[i-1], values[i])` satisfies `pairwise`. Chains always
 * return a boolean column — the default propagates without callers
 * needing to specify. */
export const defineChain = <ValueType = unknown>(
  pairwise: (a: ValueType, b: ValueType) => boolean,
  coerce?: (value: unknown) => ValueType,
  resultType: ColumnType = booleanCol,
) =>
  defineFold<ValueType>(
    (values) => values.every((v, i) => i === 0 || pairwise(values[i - 1] as ValueType, v)),
    coerce,
    resultType,
  );
/** True iff `v` is null or undefined. SSOT for the engine's
 * "empty / missing value" test — an absent struct field or an
 * unfilled bucket cell arrives as `undefined`, not `null`, so every
 * null check treats the two alike (matches `isTruthy`'s falsy set). */
export const isNullish = (v: unknown): boolean => v === null || v === undefined;
/** Null-propagating numeric op. Any null/undefined operand ⇒ `null`
 * (no throw — consistent with the `to-date`/`coalesce` null
 * convention); otherwise every arg is coerced to `number` and `fn` is
 * applied. Built on `defineFold` with the identity coerce so the
 * apply path (schema-resolved `unknown` params) and the compileExpr
 * path (per-row values) share one reducer. Result type is always
 * `number`. Schemas must use `z.unknown()` params (NOT
 * `z.coerce.number()`) so a null survives to the reducer instead of
 * being coerced to 0. Coercion is the box-aware `cellNumber`, so a
 * unit-bearing operand contributes its bare number. An optional
 * `unitPolicy` propagates operand units onto the result at value level. */
export const defineNumeric = (fn: (nums: readonly number[]) => number, unitPolicy?: UnitPolicy) =>
  defineFold<unknown>((vs) => (vs.some(isNullish) ? null : fn(vs.map(cellNumber))), undefined, numberCol, unitPolicy);
// Symbol reference shape used by `dataset` and pipeline verbs to capture
// names structurally (rather than via env lookup). The parser emits symbols
// as `{ $sym: <name> }`; bindings/verbs accept that shape without
// evaluating it.
export const SymbolRef = z.object({ $sym: z.string() });
